import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { nhomNghin } from '../../format';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import { ArrowsLeftRight, Plus, Prohibit, Question } from '@phosphor-icons/react';
import { StatusTag } from '../../components/StatusTag';
import dayjs, { type Dayjs } from 'dayjs';
import {
  ROUNDING_LABELS,
  SEED_TIER_LEVELS,
  canRetireVersion,
  describeVersionState,
  multiplierFor,
  type EarningRuleVersion,
  type EarningRules,
  type RoundingMode,
} from './api';
import { tierApi, tierKeys, type TierDefinition } from '../tiers/api';
import { tierColorFor, tokens } from '../../theme';

/** Một cột hệ số trên form. `name` chỉ để hiển thị; engine tra theo `code`. */
interface TierColumn {
  code: string;
  name: string;
}

/**
 * Tên hiển thị của một mã hạng, tìm ngược trong MỌI bản thang hạng — kể cả bản đã bị thay thế.
 *
 * Cần vì bản quy tắc cũ khai hệ số cho hạng nay đã bỏ (`bronze`): thang hạng đang áp không còn
 * mã đó, nhưng bản thang cũ thì còn, và hiện `bronze` kèm tên "Đồng" dễ đọc hơn hẳn mã trần.
 * Không tìm thấy ở đâu thì trả về chính mã — thà thô còn hơn bịa một cái tên.
 */
function tierNameFor(code: string, definitions: TierDefinition[] | undefined): string {
  for (const definition of definitions ?? []) {
    const level = definition.levels.find((l) => l.code === code);
    if (level) return level.name;
  }
  return code;
}

interface RuleFormPanelProps {
  /**
   * Bản đang hiện ở form. `null` khi đang tạo mới (`isCreating`) hoặc chưa có bản nào —
   * KHÔNG còn nghĩa là "đóng" như bản Drawer cũ, panel này luôn hiện trên trang.
   */
  version: EarningRuleVersion | null;
  isCreating: boolean;
  chains: { id: string; name: string }[];
  submitting: boolean;
  onSubmit: (values: SubmitValues) => void;
  // Hủy/Lưu bản nháp/Phát hành chuyển lên PageHeader ở trang cha (giống prototype), gọi thẳng
  // hàm của trang cha — panel chỉ còn lộ `submit()` qua ref (xem RuleFormPanelHandle) cho nút
  // "Lưu bản nháp" ở đó dùng. "Ngừng bản này" vẫn ở lại panel vì không nằm trong 3 nút đó.
  onRetire: () => void;
  retiring: boolean;

  /** Sidebar "Lịch sử phiên bản" — dữ liệu và điều khiển nằm ở đây để chỉ có MỘT nơi vẽ layout g-2-1. */
  versions: EarningRuleVersion[];
  selectedId: string | null;
  onSelectVersion: (v: EarningRuleVersion) => void;
  onCreateNew: () => void;
  onCompare: () => void;
  compareDisabled: boolean;
}

export interface SubmitValues {
  version: string;
  chainId: string | null;
  rules: EarningRules;
  effectiveFrom: string;
  note: string | null;
}

interface FormShape {
  version: string;
  chainId: string | null;
  vndPerPoint: number;
  minBillAmount: number;
  dailyEarnCap: number;
  rounding: RoundingMode;
  effectiveFrom: Dayjs;
  note?: string;
  tiers: Record<string, number>;
}

/**
 * Bộ số khởi điểm, điền sẵn cho bản nháp đầu tiên.
 *
 * `tierMultipliers` để RỖNG: hạng thẻ không nhân hệ số nữa, nên mọi bậc khởi điểm ở 1,0. Bản cũ
 * điền sẵn 1,2/1,5/2,0 — người tạo bản nháp mới sẽ vô tình phát hành lại hệ số đã bỏ, chỉ vì form
 * điền hộ.
 */
const SEED_DEFAULTS: EarningRules = {
  vndPerPoint: 10_000,
  minBillAmount: 50_000,
  dailyEarnCap: 2_000,
  rounding: 'Floor',
  tierMultipliers: {},
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Form sửa/xem MỘT bản quy tắc, cộng sidebar lịch sử — bố cục 2 cột giống prototype
 * (`a-earning`, `g-2-1`), nằm THẲNG trên trang thay vì trong Drawer trượt ra (đổi 2026-09-01).
 *
 * <p>🔴 <b>Không đổi bất biến BR-105/ADR-006</b> — bản đã phát hành vẫn khóa nội dung
 * (`readOnly`), sửa chỉ khả thi trên bản NHÁP. Đổi ở đây thuần là vị trí form (trang chính
 * thay vì Drawer) và cách chọn bản cũ (sidebar chấm màu thay vì bảng lớn), không đổi hành vi.</p>
 */
/** Cho trang cha (EarningRulesPage) gọi "Lưu bản nháp" từ nút đặt ở PageHeader, giống prototype. */
export interface RuleFormPanelHandle {
  submit: () => void;
}

export const RuleFormPanel = forwardRef<RuleFormPanelHandle, RuleFormPanelProps>(function RuleFormPanel({
  version,
  isCreating,
  chains,
  submitting,
  onSubmit,
  onRetire,
  retiring,
  versions,
  selectedId,
  onSelectVersion,
  onCreateNew,
  onCompare,
  compareDisabled,
}, ref) {
  const [form] = Form.useForm<FormShape>();

  const readOnly = version?.isPublished === true;
  const hasContent = isCreating || version !== null;

  // Thang hạng THẬT, không phải hằng số trong code. Trước 2026-09-11 chỗ này vẽ cứng bốn bậc
  // Đồng/Bạc/Vàng/Kim Cương; chính sách rút còn ba bậc thì form vẫn hiện bốn ô, và ô thừa gửi
  // lên một mã hạng không tồn tại — `MultiplierFor` tra không thấy nên hệ số đó không bao giờ
  // được áp, im lặng. Đúng loại lỗi mà `WarnOnMultiplierMismatchAsync` bên backend phải sinh ra
  // để cảnh báo.
  const tiersQuery = useQuery({ queryKey: tierKeys.all, queryFn: tierApi.list });

  const tierLevels = useMemo<TierColumn[]>(() => {
    // Bản ĐÃ PHÁT HÀNH: hiện đúng những hạng mà CHÍNH BẢN ĐÓ khai, không phải thang hạng hôm
    // nay. Lấy theo thang hôm nay là bịa lại lịch sử — bản 2026.13 có hệ số cho `bronze` thì
    // người xem phải thấy `bronze`, dù hạng đó đã bỏ.
    if (version?.isPublished) {
      const keys = Object.keys(version.rules.tierMultipliers ?? {});
      if (keys.length > 0) {
        return keys.map((code) => ({ code, name: tierNameFor(code, tiersQuery.data) }));
      }
      return [];
    }

    // Bản nháp / tạo mới: theo thang hạng ĐANG ÁP DỤNG.
    const effective =
      tiersQuery.data?.find((t) => t.isCurrentlyEffective) ??
      tiersQuery.data?.find((t) => t.isPublished);

    if (effective) {
      return effective.levels.map((l) => ({ code: l.code, name: l.name }));
    }

    // Chưa tải xong hoặc chưa khai thang nào — dùng bộ seed để form không rỗng.
    return SEED_TIER_LEVELS.map((t) => ({ code: t.code, name: t.name }));
  }, [version, tiersQuery.data]);

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) =>
        onSubmit({
          version: values.version.trim(),
          chainId: values.chainId ?? null,
          effectiveFrom: values.effectiveFrom.toISOString(),
          note: values.note?.trim() || null,
          rules: {
            vndPerPoint: values.vndPerPoint,
            minBillAmount: values.minBillAmount,
            dailyEarnCap: values.dailyEarnCap,
            rounding: values.rounding,
            // Gửi mã hạng, không gửi tên — API chặn phát hành khi khóa không khớp `TierLevel.Code`.
            // Chỉ gửi những hạng ĐANG CÓ trên thang, nên không đẻ ra hệ số mồ côi.
            tierMultipliers: Object.fromEntries(
              tierLevels.map((tier) => [tier.code, values.tiers[tier.code] ?? 1]),
            ),
          },
        }),
      )
      // Ô không hợp lệ đã hiện lỗi ngay dưới nó — nuốt để không thành unhandled rejection
      .catch(() => {});
  };

  useImperativeHandle(ref, () => ({ submit: handleSubmit }), [handleSubmit]);

  useEffect(() => {
    if (!hasContent) return;

    const rules = version?.rules ?? SEED_DEFAULTS;
    form.setFieldsValue({
      version: version?.version ?? dayjs().format('YYYY.MM'),
      chainId: version?.chainId ?? null,
      vndPerPoint: rules.vndPerPoint,
      minBillAmount: rules.minBillAmount,
      dailyEarnCap: rules.dailyEarnCap,
      rounding: rules.rounding,
      effectiveFrom: version ? dayjs(version.effectiveFrom) : dayjs().add(1, 'day').startOf('hour'),
      note: version?.note ?? undefined,
      tiers: Object.fromEntries(
        tierLevels.map((tier) => [
          tier.code,
          multiplierFor(rules.tierMultipliers, tier.code, tier.name),
        ]),
      ),
    });
    // `tierLevels` nằm trong deps: thang hạng tải xong SAU lần render đầu, không nạp lại thì các ô
    // hệ số đứng im ở bộ seed.
  }, [hasContent, version, form, tierLevels]);

  return (
    <>
      {/* Tiêu đề nổi bật "Phiên bản X · trạng thái" — giống prototype (a-earning: "Phiên bản 4
          · đang soạn thảo"), trước đây số hiệu chỉ nằm lẫn trong 1 ô nhập, không nổi bật. */}
      {hasContent && (
        <div style={{ marginBottom: tokens.space.sectionGap }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {version ? version.version : 'Bản nháp mới'}
            {version && (
              <StatusTag color={describeVersionState(version, versions).tone} style={{ marginLeft: 10, verticalAlign: 'middle' }}>
                {describeVersionState(version, versions).label}
              </StatusTag>
            )}
          </Typography.Title>
        </div>
      )}

      <Row gutter={[tokens.space.sectionGap, tokens.space.sectionGap]}>
      <Col xs={24} lg={14}>
        {!hasContent ? (
          <Card size="small">
            <Empty description="Chưa có bản quy tắc nào. Tạo bản mới để bắt đầu tích điểm.">
              <Button type="primary" icon={<Plus size={14} aria-hidden />} onClick={onCreateNew}>
                Tạo bản mới
              </Button>
            </Empty>
          </Card>
        ) : (
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            disabled={readOnly || submitting}
            // Bản đã phát hành khoá bằng `disabled`, nhưng "đã phát hành" KHÔNG phải "không dùng
            // được" — đó là con số đang áp cho mọi hóa đơn, thứ người ta vào đây để ĐỌC. Màu xám
            // 25% của antd nói sai điều đó và làm chữ gần như không đọc nổi. Class này trả lại độ
            // tương phản; xem `index.css`.
            className={readOnly ? 'reski-readonly-form' : undefined}
          >
            {readOnly && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: tokens.space.sectionGap }}
                message="Bản đã phát hành — chỉ xem"
                description="Sửa bản đã phát hành sẽ đổi cách tính của những giao dịch đã xong. Muốn đổi con số thì tạo bản mới với ngày hiệu lực mới."
              />
            )}

            {/*
              Bỏ chữ "theo hạng" khi không có hệ số nào: tiêu đề hứa một thứ mà cả thẻ không có
              dòng nào nói tới thì người đọc sẽ đi tìm — và tưởng màn hình lỗi.
            */}
            <Card
              size="small"
              title={tierLevels.length > 0 ? 'Tỷ lệ tích điểm theo hạng' : 'Tỷ lệ tích điểm'}
              style={{ marginBottom: tokens.space.sectionGap }}
            >
              <Form.Item
                name="vndPerPoint"
                label="Số tiền đổi 1 điểm (đồng)"
                tooltip="Ví dụ: để 10.000đ thì hóa đơn 500.000đ được 50 điểm."
                rules={[
                  { required: true, message: 'Chưa nhập số tiền đổi 1 điểm.' },
                  { type: 'number', min: 1, message: 'Phải lớn hơn 0.' },
                ]}
              >
                <InputNumber style={{ width: '100%' }} min={1} step={1000} formatter={nhomNghin.formatter} parser={nhomNghin.parser} />
              </Form.Item>

              <Form.Item
                name="minBillAmount"
                label="Hóa đơn tối thiểu để tích điểm (đồng)"
                tooltip="Hóa đơn thấp hơn mức này thì không tích điểm nào, kể cả điểm xét hạng."
                rules={[
                  { required: true, message: 'Chưa nhập hóa đơn tối thiểu.' },
                  { type: 'number', min: 0, message: 'Không được âm.' },
                ]}
              >
                <InputNumber style={{ width: '100%' }} min={0} step={10_000} formatter={nhomNghin.formatter} parser={nhomNghin.parser} />
              </Form.Item>

              {/*
                Bản không đặt hệ số theo hạng (`tierMultipliers` rỗng — bình thường từ 2026-09-11)
                thì ẨN HẲN cả khu vực này: đoạn giải thích lẫn các ô hệ số.

                Trước đó chỗ này hiện một dòng thông báo "bản này không đặt hệ số". Bỏ đi theo PL
                2026-09-11: một dòng nói-về-cái-không-có vẫn chiếm chỗ và vẫn bắt người đọc dừng
                lại xử lý, trong khi hệ số theo hạng nay là ngoại lệ chứ không phải mặc định.
                Không có gì để khai thì không có gì để nói.

                📌 Đoạn văn giải thích cũng đi theo — nó nói *"hệ số hạng nhân vào điểm tiêu
                được"*, mà bản này không có hệ số nào cả; để lại là mô tả một cơ chế không chạy.
              */}
              {tierLevels.length > 0 && (
                <>
                  <Typography.Paragraph type="secondary" style={{ fontSize: 12.5 }}>
                    Hệ số hạng nhân vào <strong>điểm tiêu được</strong>. <strong>Không</strong> nhân
                    vào điểm xét hạng — ngưỡng hạng phải phản ánh chi tiêu thật, nếu nhân thì lên
                    hạng xong sẽ lên tiếp nhanh hơn dù tiêu như nhau.
                  </Typography.Paragraph>

              <Row gutter={12}>
                {tierLevels.map((tier) => (
                  <Col key={tier.code} xs={24} sm={12} lg={tierLevels.length >= 4 ? 6 : 8}>
                    <Form.Item
                      name={['tiers', tier.code]}
                      label={
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span
                            aria-hidden
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: tokens.radius.pill,
                              background: tierColorFor(tier.code),
                              flex: 'none',
                            }}
                          />
                          {tier.name}
                        </span>
                      }
                      rules={[
                        { required: true, message: 'Chưa nhập hệ số.' },
                        { type: 'number', min: 0.01, message: 'Hệ số phải lớn hơn 0.' },
                      ]}
                    >
                      {/* `prefix` chứ không `addonBefore`: addonBefore đã bị AntD 5 khai tử */}
                      <InputNumber style={{ width: '100%' }} min={0.01} step={0.1} precision={2} prefix="×" />
                    </Form.Item>
                  </Col>
                ))}
              </Row>
                </>
              )}
            </Card>

            <Card
              size="small"
              title={
                <Space size={6}>
                  Giới hạn chống lạm dụng
                  <Tooltip title="Trần hóa đơn mỗi nhân viên và hạn hóa đơn hợp lệ KHÔNG nằm ở đây — chúng là thông số vận hành, đổi ở mục Thông số vận hành.">
                    <Question size={15} color={tokens.color.ink3} aria-hidden />
                  </Tooltip>
                </Space>
              }
            >
              <Row gutter={18}>
                <Col span={12}>
                  <Form.Item
                    name="dailyEarnCap"
                    label="Trần điểm mỗi khách một ngày"
                    tooltip="Một khách tích tối đa bấy nhiêu điểm mỗi ngày. Vượt thì cắt tại mức này."
                    rules={[
                      { required: true, message: 'Chưa nhập trần điểm.' },
                      { type: 'number', min: 1, message: 'Phải lớn hơn 0.' },
                    ]}
                  >
                    <InputNumber style={{ width: '100%' }} min={1} step={100} formatter={nhomNghin.formatter} parser={nhomNghin.parser} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="rounding" label="Cách làm tròn điểm">
                    <Select
                      options={(Object.keys(ROUNDING_LABELS) as RoundingMode[]).map((mode) => ({
                        value: mode,
                        label: ROUNDING_LABELS[mode],
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Form>
        )}
      </Col>

      <Col xs={24} lg={10}>
        {hasContent && (
          /* `component={false}` nên Form không dựng thẻ DOM nào — gắn class lên Card bao ngoài. */
          <Card
            size="small"
            title="Hiệu lực"
            className={readOnly ? 'reski-readonly-form' : undefined}
            style={{ marginBottom: tokens.space.sectionGap }}
          >
            <Form form={form} layout="vertical" requiredMark={false} disabled={readOnly || submitting} component={false}>
              <Form.Item
                name="version"
                label="Số hiệu phiên bản"
                tooltip="Duy nhất trong từng phạm vi. Quy ước: năm.tháng, ví dụ 2026.09."
                rules={[
                  { required: true, message: 'Chưa nhập số hiệu phiên bản.' },
                  { max: 20, message: 'Số hiệu quá dài.' },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="chainId"
                label="Phạm vi áp dụng"
                tooltip="Bản riêng của chuỗi THẮNG bản mặc định, kể cả khi bản mặc định mới hơn."
              >
                <Select
                  allowClear
                  placeholder="Toàn hệ thống (mặc định cho mọi chuỗi)"
                  options={chains.map((c) => ({ value: c.id, label: `Chỉ chuỗi ${c.name}` }))}
                />
              </Form.Item>

              <Form.Item
                name="effectiveFrom"
                label="Áp dụng từ"
                tooltip="Đặt được ở tương lai. Nên chọn sau giờ đóng cửa để không đổi quy tắc giữa ca."
                rules={[{ required: true, message: 'Chưa chọn thời điểm hiệu lực.' }]}
              >
                <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="note"
                label="Ghi chú"
                tooltip="Vì sao đổi. Sáu tháng sau nhìn lại một con số lạ mà không có lý do thì không ai dám sửa."
                rules={[{ max: 500, message: 'Ghi chú quá dài.' }]}
              >
                <Input.TextArea rows={2} />
              </Form.Item>
            </Form>

            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: readOnly ? 0 : tokens.space.sectionGap }}
              message="Thay đổi chỉ áp dụng cho giao dịch phát sinh SAU thời điểm hiệu lực"
              description="Giao dịch cũ giữ nguyên quy tắc đã áp, nên số điểm đã cộng không bao giờ bị tính lại."
            />

            {/* Hủy/Lưu bản nháp/Phát hành chuyển lên PageHeader (giống prototype a-earning) —
                xem RuleFormPanelHandle.submit và EarningRulesPage.tsx. */}

            {readOnly && version && canRetireVersion(version, versions) && (
              <Button
                danger
                style={{ marginTop: tokens.space.sectionGap }}
                icon={<Prohibit size={14} aria-hidden />}
                loading={retiring}
                onClick={onRetire}
              >
                Ngừng bản này
              </Button>
            )}
          </Card>
        )}

        <Card
          size="small"
          title="Lịch sử phiên bản"
          extra={
            <Space>
              <Button size="small" icon={<ArrowsLeftRight size={14} aria-hidden />} disabled={compareDisabled} onClick={onCompare}>
                So sánh
              </Button>
              <Button size="small" type="primary" icon={<Plus size={14} aria-hidden />} onClick={onCreateNew}>
                Bản mới
              </Button>
            </Space>
          }
        >
          {versions.length === 0 ? (
            <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
              Chưa có bản nào.
            </Typography.Text>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {versions.map((v) => {
                const state = describeVersionState(v, versions);
                const isSelected = v.id === selectedId;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => onSelectVersion(v)}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: '8px 6px',
                      border: 'none',
                      borderRadius: tokens.radius.control,
                      background: isSelected ? tokens.color.green50 : 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      font: 'inherit',
                      width: '100%',
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: tokens.radius.pill,
                        marginTop: 5,
                        flex: 'none',
                        background:
                          state.tone === 'success'
                            ? tokens.color.ok
                            : state.tone === 'warning'
                              ? tokens.color.warn
                              : tokens.color.ink3,
                      }}
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{v.version}</span>{' '}
                      <StatusTag color={state.tone} style={{ marginLeft: 2 }}>
                        {state.label}
                      </StatusTag>
                      <div style={{ fontSize: 12, color: tokens.color.ink3 }}>
                        {v.chainName ? `Chỉ chuỗi ${v.chainName}` : 'Toàn hệ thống'} · {formatDateTime(v.effectiveFrom)}
                      </div>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </Card>
      </Col>
      </Row>
    </>
  );
});
